import * as THREE from 'three';

// ─── Weather Simulation ─────────────────────────────────────────────────────
// Rain particles, snow particles, wind rating visualization,
// and auto-close roof behavior on weather events.

export class WeatherSimulation {
  constructor(scene) {
    this.scene = scene;
    this.activeWeather = null; // 'rain' | 'snow' | null
    this.particles = null;
    this.particleMaterial = null;
    this.animationFrame = null;
    this.onRoofCloseCallback = null;
    this.snowOption = 'open'; // 'open' | 'closed'

    // Particle system bounds
    this.bounds = { width: 8, height: 6, depth: 8, y: 5 };
  }

  setRoofCloseCallback(cb) {
    this.onRoofCloseCallback = cb;
  }

  // ─── Rain ────────────────────────────────────────────────────────
  startRain() {
    this.stopWeather();
    this.activeWeather = 'rain';

    const count = 3000;
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * this.bounds.width;
      positions[i * 3 + 1] = Math.random() * this.bounds.height + this.bounds.y;
      positions[i * 3 + 2] = (Math.random() - 0.5) * this.bounds.depth;
      velocities[i] = 0.08 + Math.random() * 0.06;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.userData = { velocities };

    this.particleMaterial = new THREE.PointsMaterial({
      color: 0x8899bb,
      size: 0.03,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
    });

    this.particles = new THREE.Points(geo, this.particleMaterial);
    this.scene.add(this.particles);

    // Auto-close roof on rain
    if (this.onRoofCloseCallback) {
      setTimeout(() => this.onRoofCloseCallback(), 500);
    }
  }

  // ─── Snow ────────────────────────────────────────────────────────
  startSnow(option = 'open') {
    this.stopWeather();
    this.activeWeather = 'snow';
    this.snowOption = option;

    const count = 1500;
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count);
    const drifts = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * this.bounds.width;
      positions[i * 3 + 1] = Math.random() * this.bounds.height + this.bounds.y;
      positions[i * 3 + 2] = (Math.random() - 0.5) * this.bounds.depth;
      velocities[i] = 0.01 + Math.random() * 0.015;
      drifts[i] = (Math.random() - 0.5) * 0.005;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.userData = { velocities, drifts };

    this.particleMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.05,
      transparent: true,
      opacity: 0.85,
    });

    this.particles = new THREE.Points(geo, this.particleMaterial);
    this.scene.add(this.particles);

    // Snow behavior depends on option
    if (option === 'open' && this.onRoofCloseCallback) {
      // Leave open — snow passes through (blade angle stays open or goes to 170)
    } else if (option === 'closed' && this.onRoofCloseCallback) {
      // Close roof — snow accumulates on top
      setTimeout(() => this.onRoofCloseCallback(), 300);
    }
  }

  // ─── Animate particles (call each frame) ─────────────────────────
  update() {
    if (!this.particles || !this.activeWeather) return;

    const pos = this.particles.geometry.attributes.position;
    const arr = pos.array;
    const { velocities, drifts } = this.particles.geometry.userData;

    for (let i = 0; i < pos.count; i++) {
      // Y (fall)
      arr[i * 3 + 1] -= velocities[i];

      // Snow drift (X wobble)
      if (this.activeWeather === 'snow' && drifts) {
        arr[i * 3] += drifts[i];
        arr[i * 3] += Math.sin(Date.now() * 0.001 + i) * 0.001;
      }

      // Rain slight wind
      if (this.activeWeather === 'rain') {
        arr[i * 3] += 0.002; // slight eastward wind
      }

      // Reset when below ground
      if (arr[i * 3 + 1] < -0.5) {
        arr[i * 3] = (Math.random() - 0.5) * this.bounds.width;
        arr[i * 3 + 1] = this.bounds.y + Math.random() * this.bounds.height;
        arr[i * 3 + 2] = (Math.random() - 0.5) * this.bounds.depth;
      }
    }

    pos.needsUpdate = true;
  }

  // ─── Stop ────────────────────────────────────────────────────────
  stopWeather() {
    if (this.particles) {
      this.scene.remove(this.particles);
      if (this.particles.geometry) this.particles.geometry.dispose();
      if (this.particleMaterial) this.particleMaterial.dispose();
      this.particles = null;
      this.particleMaterial = null;
    }
    this.activeWeather = null;
  }

  isActive() {
    return this.activeWeather !== null;
  }

  getActiveWeather() {
    return this.activeWeather;
  }
}

// ─── Wind / Snow Load Info Data ─────────────────────────────────────────────
// For the info popup graphics — educational data about wind/snow ratings

export const WIND_LOAD_INFO = {
  title: 'Wind Load Ratings',
  description: 'Louver position affects wind resistance. Open louvers catch more wind than closed.',
  ratings: [
    { position: 'Closed (0°)', rating: '110 mph', note: 'Maximum wind resistance — louvers sealed flat' },
    { position: 'Partially Open (45°)', rating: '75 mph', note: 'Moderate wind load — partial sail effect' },
    { position: 'Fully Open (170°)', rating: '50 mph', note: 'Highest wind load — maximum sail area' },
  ],
  bestPractice: 'In high wind conditions, close the louvers to minimize wind load. The R-Blade system can be automated to close when wind exceeds a threshold using the Bond Breeze Multi Sensor.',
};

export const SNOW_LOAD_INFO = {
  title: 'Snow Load Ratings',
  description: 'The R-Blade pergola is rated for snow loads. Think of it like this:',
  equivalents: [
    { snow: '1" fresh snow', equivalent: '~1 lb/sq ft', relatable: 'Like a thin blanket of cotton' },
    { snow: '6" fresh snow', equivalent: '~3 lb/sq ft', relatable: 'Like a bag of flour per 4 sq ft' },
    { snow: '12" packed snow', equivalent: '~15 lb/sq ft', relatable: 'Like a small child standing on every 4x4 area' },
    { snow: '6" ice', equivalent: '~30 lb/sq ft', relatable: 'Like a large dog on every 4x4 area' },
  ],
  options: {
    open: {
      label: 'Option A: Leave Open',
      description: 'Let snow pass through the open louvers. Resume use immediately when snow clears.',
      animation: 'Snow falls through open blades to ground below.',
    },
    closed: {
      label: 'Option B: Leave Closed',
      description: 'Keep louvers closed. Snow accumulates on roof. MUST wait for full thaw before operating to avoid motor damage.',
      animation: 'Snow accumulates on closed roof surface. Warning shown about thaw requirement.',
      warning: 'Do NOT operate louvers until all snow/ice has completely melted to avoid motor damage.',
    },
  },
};
